import axios from "axios";
import AuthService from './AuthService';

// จัดการ "เซสชันหมดอายุ" ไว้ที่เดียว แทนที่จะกระจายอยู่ตาม component
//
// มี 2 สาเหตุที่ทำให้ผู้ใช้หลุด และทั้งคู่ต้องจบด้วยการกลับไปหน้า login
//   1. access token ของ Cognito หมดอายุ  -> ตรวจจาก exp ใน JWT เป็นหลัก
//   2. ข้ามวันแล้ว (นโยบายตัด session รายวัน) -> ตรวจจากวันที่ที่เก็บไว้ตอน login

const SESSION_EXPIRED = {
    title: 'Session หมดอายุ',
    message: 'เพื่อความปลอดภัยของข้อมูล ระบบได้ออกจากระบบให้อัตโนมัติ กรุณาเข้าสู่ระบบอีกครั้งเพื่อใช้งานต่อ',
    button: 'เข้าสู่ระบบอีกครั้ง',
};

const LOGGED_OUT_ELSEWHERE = {
    title: 'ออกจากระบบแล้ว',
    message: 'บัญชีนี้ออกจากระบบจากแท็บอื่นแล้ว กรุณาเข้าสู่ระบบอีกครั้งเพื่อใช้งานต่อ',
    button: 'เข้าสู่ระบบอีกครั้ง',
};

// สีปุ่มให้เข้ากับธีมของแอป แก้ที่เดียวได้ทั้งกล่อง
const ACCENT_COLOR = '#DC67C2';

// เผื่อนาฬิกาเครื่องคลาดจาก server เล็กน้อย
const CLOCK_SKEW_MS = 5000;

// กันไม่ให้ redirect/กล่องข้อความซ้อนกัน เวลาที่หลาย request พังพร้อมกัน
let redirecting = false;

const DIALOG_ID = 'session-guard-dialog';

// ใช้ DOM ตรง ๆ ไม่ผ่าน React เพราะเคส logout ข้ามแท็บ WebRoute จะ render
// หน้าว่างไปแล้ว (ทุก route ผูกกับ getCurrentUser()) กล่องที่อยู่นอก React
// จึงยังอยู่ให้ผู้ใช้อ่านได้ และ window.alert() ก็กำหนด title/ปุ่มเองไม่ได้
const showDialog = (notice, onConfirm) => {
    if (document.getElementById(DIALOG_ID)) return;

    const overlay = document.createElement('div');
    overlay.id = DIALOG_ID;
    overlay.setAttribute('role', 'alertdialog');
    overlay.setAttribute('aria-modal', 'true');
    Object.assign(overlay.style, {
        position: 'fixed', inset: '0', zIndex: '2147483647',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', background: 'rgba(0, 0, 0, 0.45)',
    });

    const card = document.createElement('div');
    Object.assign(card.style, {
        maxWidth: '420px', width: '100%', boxSizing: 'border-box',
        padding: '28px', borderRadius: '14px', background: '#ffffff',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.25)', textAlign: 'center',
        font: 'inherit', color: '#333333',
    });

    const title = document.createElement('div');
    title.textContent = notice.title;
    Object.assign(title.style, { fontSize: '20px', fontWeight: '700', marginBottom: '12px' });

    const message = document.createElement('div');
    message.textContent = notice.message;
    Object.assign(message.style, { fontSize: '15px', lineHeight: '1.7', color: '#555555', marginBottom: '24px' });

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = notice.button;
    Object.assign(button.style, {
        font: 'inherit', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
        padding: '11px 28px', borderRadius: '999px', border: 'none',
        background: ACCENT_COLOR, color: '#ffffff',
    });

    // redirect ก็ต่อเมื่อผู้ใช้กดปุ่มเองเท่านั้น ระหว่างนี้ยังเห็นหน้าเดิมอยู่ข้างหลัง
    button.addEventListener('click', () => {
        overlay.remove();
        onConfirm();
    });

    card.appendChild(title);
    card.appendChild(message);
    card.appendChild(button);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    button.focus();
};

const performReLogin = (notice) => {
    const finish = () => {
        AuthService.logout();
        window.location.href = '/login';
    };
    // ไม่มีข้อความ = เคสเงียบ (เปิดหน้าใหม่ตอนเช้าแล้วข้ามวัน) ไปเลยไม่ต้องถาม
    if (!notice) return finish();
    showDialog(notice, finish);
};

const forceReLogin = (notice) => {
    if (redirecting) return;
    // อยู่หน้า login อยู่แล้วไม่ต้องทำอะไร กันเด้ง/กล่องข้อความวนซ้ำ
    if (window.location.pathname === '/login') return;
    redirecting = true;
    // กล่องนี้เป็น DOM ธรรมดา แท็บที่อยู่เบื้องหลังก็แสดงได้ (ต่างจาก alert()
    // ที่เบราว์เซอร์กลืนทิ้ง) จึงไม่ต้องรอให้ผู้ใช้สลับกลับมาก่อน
    performReLogin(notice);
};

// --- อ่าน token และวันหมดอายุ ---------------------------------------------

// ทั้งสองทางที่ login เก็บ access_token ไว้คนละรูปแบบ
//   AuthService.login  -> JSON.stringify(...) จึงมีเครื่องหมาย " ครอบ
//   CognitoLogin       -> เก็บ jwtToken ดิบ ๆ
const readStoredToken = () => {
    const raw = localStorage.getItem('access_token');
    if (!raw) return '';
    let token = raw;
    if (token.charAt(0) === '"') {
        try {
            token = JSON.parse(token);
        } catch (e) {
            // ค่าเสีย ใช้ของเดิมต่อไป
        }
    }
    return String(token).replace(/^Bearer\s+/i, '').trim();
};

const decodeJwtPayload = (token) => {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '==='.slice((base64.length + 3) % 4);
        return JSON.parse(window.atob(padded));
    } catch (e) {
        return null;
    }
};

// null = ไม่รู้ (ไม่มี token หรืออ่าน exp ไม่ได้) จะได้ไม่เผลอเตะคนที่ยังใช้งานได้อยู่
const getTokenExpiry = () => {
    const token = readStoredToken();
    if (!token) return null;
    const payload = decodeJwtPayload(token);
    if (!payload || !payload.exp) return null;
    return payload.exp * 1000;
};

const isTokenExpired = () => {
    const expiresAt = getTokenExpiry();
    if (expiresAt === null) return false;
    return Date.now() >= expiresAt - CLOCK_SKEW_MS;
};

// ผู้ใช้ที่ไม่ได้ login เลย (ลิงก์ประเมินแบบ public) ไม่ต้องเด้งไปหน้า login
// เช็ค access_token ด้วย เพราะบางเคส object user หายไปก่อนแต่ token ยังค้างอยู่
const hasSession = () => Boolean(AuthService.getCurrentUser() || readStoredToken());

// logout() เรียก localStorage.clear() ทำให้แท็บอื่นเหลือแต่ state ในหน่วยความจำ
// โดยที่ hasSession() กลายเป็น false ไปแล้ว ถ้าดูแค่ค่าปัจจุบันจะแยกไม่ออกว่า
// เป็นผู้ใช้ public ที่ไม่เคย login หรือเป็นคนที่เพิ่งถูกเตะออก จึงต้องจำไว้
let sawSession = false;

const rememberSession = () => {
    if (hasSession()) sawSession = true;
};

// แท็บนี้เคย login แล้วตอนนี้ localStorage ว่าง = ถูก logout จากที่อื่น
const sessionWasTerminated = () => sawSession && !hasSession();

// --- ตรวจจาก error response ----------------------------------------------

// backend ไม่ได้ตอบ 401 เสมอไป เมื่อ resolve ผู้ใช้จาก token ไม่ได้
// endpoint ที่ต้องใช้ token จะตอบ 500 SERVER_ERROR พร้อม message
// ทำนอง "User must not be null" ออกมาแทน จึงต้องดักจากข้อความด้วย
// ใช้ \b ครอบ เพื่อไม่ให้ไป match ฟิลด์อื่นที่ขึ้นต้นด้วย user
// เช่น "userAnswer must not be null" ซึ่งเป็น bug ฝั่ง server จริง ๆ
// ไม่ใช่เซสชันหมด ถ้าเผลอ match จะเตะผู้ใช้ออกทั้งที่ยัง login อยู่
const NULL_USER_PATTERN = /\buser\b[^.]{0,40}\bnull\b|\bnull\b[^.]{0,20}\buser\b/i;

const readCode = (data) => (data && data.status && data.status.code) || '';

const readMessage = (data) => {
    if (!data) return '';
    if (typeof data === 'string') return data;
    if (data.status && data.status.message) return data.status.message;
    return data.message || data.error || '';
};

const isSessionExpiredError = (error) => {
    const response = error && error.response;
    if (!response) return false;

    if (response.status === 401 || response.status === 403) return true;
    if (/unauthorized/i.test(readCode(response.data))) return true;

    if (response.status >= 500) {
        if (NULL_USER_PATTERN.test(readMessage(response.data))) return true;
        // backend ตอบ 500 กลาง ๆ โดยไม่บอกสาเหตุ ถ้า token ที่เก็บไว้หมดอายุแล้ว
        // ก็แปลว่าผู้ใช้ต้อง login ใหม่อยู่ดี ไม่ต้องรอให้ backend บอก
        if (isTokenExpired()) return true;
    }

    return false;
};

// --- ตรวจการข้ามวัน ----------------------------------------------------

// notify = true เมื่อผู้ใช้กำลังใช้งานอยู่แล้วเพิ่งข้ามวัน (กลับมาที่แท็บ)
// ถ้าเป็นการเปิดหน้าใหม่ตอนเช้าไม่ต้องขึ้นข้อความ จะได้ไม่รบกวน
const enforceDailySession = (notify = false) => {
    if (!AuthService.getCurrentUser()) return;
    if (AuthService.isLoginDateStale() || isTokenExpired()) {
        forceReLogin(notify ? SESSION_EXPIRED : null);
    }
};

// เดิมเช็คแค่ตอน mount ของ WebRoute ทำให้แท็บที่เปิดค้างข้ามคืน
// (เคสที่พบบ่อยบนเครื่องส่วนกลางในโรงเรียน) ไม่เคยถูกตัดเลย
const watchDailySession = () => {
    const onVisible = () => {
        if (document.visibilityState === 'visible') {
            enforceDailySession(true);
        }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('focus', onVisible);
    };
};

// --- ฟังการ logout จากแท็บอื่น -------------------------------------------

// event 'storage' ยิงเฉพาะแท็บอื่นที่ไม่ได้เป็นคนแก้ค่า จึงใช้จับเคสนี้ได้พอดี
// logout() เรียก localStorage.clear() -> event.key จะเป็น null
const onStorageChanged = (event) => {
    if (!sawSession) return;
    const clearedAll = event.key === null;
    const droppedUser = (event.key === 'user' || event.key === 'access_token') && !event.newValue;
    if (!clearedAll && !droppedUser) return;
    if (hasSession()) return;
    forceReLogin(LOGGED_OUT_ELSEWHERE);
};

const watchOtherTabs = () => {
    window.addEventListener('storage', onStorageChanged);
    return () => window.removeEventListener('storage', onStorageChanged);
};

// --- ติดตั้ง interceptor --------------------------------------------------

let installed = false;

const install = () => {
    if (installed) return;
    installed = true;

    axios.interceptors.response.use(
        (response) => {
            rememberSession();
            return response;
        },
        (error) => {
            const terminated = sessionWasTerminated();
            if (terminated || (hasSession() && isSessionExpiredError(error))) {
                forceReLogin(terminated ? LOGGED_OUT_ELSEWHERE : SESSION_EXPIRED);
                // คืน promise ที่ไม่ settle เพื่อไม่ให้ catch เดิมของแต่ละหน้า
                // ขึ้น alert "กรุณา capture หน้าจอ" ซ้อนขึ้นมาอีกอัน
                // ระหว่างที่กำลัง redirect
                return new Promise(() => { });
            }

            if (process.env.NODE_ENV !== 'production' && error && error.response && error.response.status >= 500) {
                // ช่วยตอนไล่เคสที่ยังหลุดรอดไปขึ้น alert เดิม
                console.warn('[SessionGuard] ปล่อย 5xx ผ่านไป', {
                    url: error.config && error.config.url,
                    code: readCode(error.response.data),
                    message: readMessage(error.response.data),
                    data: error.response.data,
                    hasSession: hasSession(),
                    sawSession,
                    sessionWasTerminated: sessionWasTerminated(),
                    tokenExpiry: getTokenExpiry() && new Date(getTokenExpiry()).toString(),
                    tokenExpired: isTokenExpired(),
                });
            }

            return Promise.reject(error);
        }
    );

    rememberSession();
    watchOtherTabs();

    if (process.env.NODE_ENV !== 'production') {
        // เรียกจาก console ได้: __sessionGuard.getTokenExpiry()
        window.__sessionGuard = {
            getTokenExpiry, isTokenExpired, hasSession, readStoredToken,
            sessionWasTerminated, sawSession: () => sawSession,
        };
    }
};

const SessionGuard = {
    install, isSessionExpiredError, enforceDailySession, watchDailySession,
    isTokenExpired, getTokenExpiry, sessionWasTerminated, watchOtherTabs,
};

export default SessionGuard;
